import { useCallback, useMemo } from 'react';
import { Filter } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { useSearchParams } from 'react-router-dom';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import type { MultipleChoiceFilterOption } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilterTypes';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import type { StudentPlacementStatus } from '@duke-hq/svc-api-db';
import { placementStatusMetadata } from '@duke-hq/ui/components/PlacementStatusBadge/PlacementStatusBadge';

export function usePlacementStatusFilter(): StudentPlacementStatus[] {
  const [searchParams] = useSearchParams();
  return useMemo(() => {
    const raw = searchParams.get('statuses') ?? '';
    if (raw === '') {
      return [];
    }
    const validIds = new Set<string>(Object.keys(placementStatusMetadata));
    return raw.split(',').filter((s): s is StudentPlacementStatus => validIds.has(s));
  }, [searchParams]);
}

export function PlacementStatusFilter(): React.JSX.Element {
  const [, setSearchParams] = useSearchParams();
  const selected = usePlacementStatusFilter();
  const statusOptions = Object.entries(placementStatusMetadata).map(([id, meta]) => ({
    id,
    value: id,
    label: meta.label,
  }));

  const handleChange = useCallback(
    (filters: MultipleChoiceFilterOption) => {
      const statuses = filters.statuses ?? [];
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (statuses.length === 0) {
            next.delete('statuses');
          } else {
            next.set('statuses', statuses.join(','));
          }
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <MultipleChoiceFilter
      items={statusOptions}
      label={t`Status`}
      name="statuses"
      onFilterChange={handleChange}
      onlyUpdateOnChange
      value={selected.join(',')}
      variant="ghost"
      leftIcon={<Filter size={iconSize.md} />}
      rightIcon={undefined}
    />
  );
}
